import { Button, Col, Dropdown, Row, Sidepanel, Spinner2, Text } from 'components';
import { formatLocale, theme } from 'lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { useDispatch, useSelector } from 'react-redux';
import { ContentHeader } from 'screens/Admin/components';
import { getTiriTagitCategories, saveTiriTagitLabel } from 'services';
import styled from 'styled-components';

const Section = styled.div`
  padding: 20px 24px 0;
`;

const MediaFrame = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: ${theme.GREY_10};
  border: 1px solid ${theme.GREY_20};
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  display: block;
`;

const DetailRow = ({ label, value }) => (
  <Col noFlex marg="0 0 16px">
    <Text c1 med col={theme.GREY_60} marg="0 0 4px">
      {label}
    </Text>
    <Text>{value || '-'}</Text>
  </Col>
);

/**
 * Detail + ground-truth labeling panel for one TIRI item.
 *
 * Reads the row out of the store rather than refetching it, the same way FancamPanel does — the
 * list already holds everything the panel shows, including the media.
 */
const TiriPanel = ({ dataId, closeModal }) => {
  const { t } = useTranslation();

  // Short-circuited on dataId: this runs on every store dispatch, and without the guard a
  // closed panel still scanned the whole list — which grows with every page the list has
  // paged in — on each one.
  const item = useSelector((state) =>
    dataId ? state.admin?.tiriItems?.find((x) => x._id === dataId) : null
  );
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();

  const isVideo = item?.media?.type === 'video';
  // A Rate It-only item (a video) has no category to set. Rate It itself is not built yet, so
  // this panel is read-only for those.
  const isTaggable = !!item?.modules?.includes('tagit');

  // Tag It's working state is namespaced under `tagit` on the Tiri row so the two modules can't
  // collide over one flag or counter. Destructured once rather than optional-chained at each use.
  const { category, categoryStatus, isPoolEligible } = item?.tagit || {};

  // The 16 categories are fixed, server-owned content, so they are fetched once for the panel's
  // lifetime rather than per selected row. The ref is what makes that true: keyed on `dataId`
  // alone the effect refetched the same 16 rows every time an admin opened another item.
  const fetchedCategories = useRef(false);

  useEffect(() => {
    if (!dataId || !isTaggable || fetchedCategories.current) return;

    let cancelled = false;
    fetchedCategories.current = true;

    const fetchCategories = async () => {
      setLoadingCategories(true);
      const res = await getTiriTagitCategories();
      if (cancelled) return;

      // httpStatusSender collapses a single-key `{ categories }` payload down to the raw array,
      // and Api.get returns the response body as-is — no extra `.data` layer here. Dropdown's
      // `selected` fallback reads `.name`, so alias categoryName onto each option.
      // A failed fetch clears the latch so the next item the admin opens tries again, rather
      // than leaving the panel with an empty picker for the rest of the session.
      if (!res?.length) fetchedCategories.current = false;

      setCategories((res || []).map((c) => ({ ...c, name: c.categoryName })));
      setLoadingCategories(false);
    };

    fetchCategories();

    return () => {
      cancelled = true;
    };
  }, [dataId, isTaggable]);

  // Reset the picker to whatever this row is currently labeled with whenever the selection
  // changes, so an unsaved choice can't leak onto the next item the admin opens.
  useEffect(() => {
    if (!category) {
      setSelectedCategory(null);
      return;
    }

    setSelectedCategory({
      ...category,
      name: category.categoryName
    });
  }, [item?._id, category]);

  // The picker is reset to the row's saved category whenever the selection changes (above), so
  // "unchanged" is just the two ids matching — and an item that was never labeled reads as
  // unchanged too, since nothing is selected. After a successful save the store update below
  // moves the row's `tagit.category` onto the new value, disabling the button on its own.
  const hasChanges = !!selectedCategory && selectedCategory.categoryId !== category?.categoryId;

  const handleSave = useCallback(async () => {
    if (!selectedCategory) {
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: t('admin.tiri.panel.selectCategoryFirst') }
      });
      return;
    }

    setSaving(true);
    const res = await saveTiriTagitLabel({
      tiriId: dataId,
      categoryId: selectedCategory.categoryId
    });
    setSaving(false);

    if (!res) return;

    dispatch({ type: 'HANDLE_RES', payload: { res: t('admin.tiri.panel.saved') } });
    dispatch({
      type: 'ADMIN_EDIT_TIRI',
      payload: {
        _id: dataId,
        category: res.category,
        categoryStatus: res.categoryStatus,
        isPoolEligible: res.isPoolEligible
      }
    });
  }, [dispatch, dataId, selectedCategory, t]);

  return (
    <Sidepanel wid={640} pad="0" visible={dataId} closeModal={closeModal}>
      <ContentHeader
        text={
          !item
            ? t('admin.tiri.panel.fallbackTitle')
            : t('admin.tiri.panel.title', {
                name:
                  item.owner?.name || item.owner?.username || t('admin.tiri.panel.unknownUploader')
              })
        }
        closePanel={closeModal}
      />

      {item && (
        <Col noFlex wid="100%" scrollY>
          {item.media?.url && (
            <Section>
              <Text c1 med col={theme.GREY_60} marg="0 0 8px">
                {t('admin.tiri.fields.media')}
              </Text>
              <MediaFrame>
                {isVideo ? (
                  <ReactPlayer url={item.media.url} controls width="100%" height="320px" />
                ) : (
                  <PreviewImage src={item.media.url} alt={t('admin.tiri.panel.mediaAlt')} />
                )}
              </MediaFrame>
            </Section>
          )}

          <Section>
            <DetailRow
              label={t('admin.tiri.fields.uploader')}
              value={
                item.owner?.name
                  ? `${item.owner.name}${item.owner.username ? ` (@${item.owner.username})` : ''}`
                  : item.owner?.username
              }
            />
            <DetailRow label={t('admin.tiri.fields.team')} value={item.team?.name} />
            <DetailRow
              label={t('admin.tiri.fields.source')}
              value={t(
                `admin.tiri.panel.${item.source === 'direct' ? 'sourceDirect' : 'sourceFancam'}`
              )}
            />
            <DetailRow
              label={t('admin.tiri.fields.modules')}
              value={(item.modules || [])
                .map((m) => t(`admin.tiri.modules.${m === 'tagit' ? 'tagit' : 'rateit'}`))
                .join(' + ')}
            />
            <DetailRow label={t('admin.tiri.fields.moderation')} value={item.moderationStatus} />
            <DetailRow
              label={t('admin.tiri.fields.uploadedAt')}
              value={
                item.createdAt ? formatLocale(new Date(item.createdAt), 'LLL do yyy, hh:mm a') : '-'
              }
            />
          </Section>

          <Section>
            {!isTaggable ? (
              <Text c1 col={theme.GREY_60}>
                {t('admin.tiri.panel.noCategory')}
              </Text>
            ) : loadingCategories ? (
              <Col wid="100%" pad="20px" centerAll>
                <Spinner2 />
              </Col>
            ) : (
              <>
                <Row between noFlex marg="0 0 16px">
                  <Text c1 med col={theme.GREY_60}>
                    {t('admin.tiri.fields.status')}
                  </Text>
                  <Text b2 bold col={categoryStatus === 'LABELED' ? theme.GREEN : theme.SECONDARY}>
                    {t(`admin.tiri.status.${categoryStatus === 'LABELED' ? 'labeled' : 'pending'}`)}
                  </Text>
                </Row>

                <Row between noFlex marg="0 0 16px">
                  <Text c1 med col={theme.GREY_60}>
                    {t('admin.tiri.fields.shownToFans')}
                  </Text>
                  {/* Labeling alone isn't enough — moderation has to have passed too, so this
                      is reported separately rather than inferred from the status above. */}
                  <Text b2 bold col={isPoolEligible ? theme.GREEN : theme.GREY_60}>
                    {t(`admin.tiri.panel.${isPoolEligible ? 'yes' : 'no'}`)}
                  </Text>
                </Row>

                <Dropdown
                  title={t('admin.tiri.fields.category')}
                  admin
                  rounded
                  wid="100%"
                  bg="#fff"
                  options={categories}
                  selected={selectedCategory}
                  placeholder={t('admin.tiri.panel.categoryPlaceholder')}
                  handleSelect={setSelectedCategory}
                />

                <Row marg="20px 0 24px">
                  <Button
                    text={saving ? t('admin.tiri.panel.saving') : t('admin.tiri.panel.save')}
                    onClick={handleSave}
                    bg={theme.PRIMARY}
                    loading={saving}
                    disabled={!hasChanges}
                    wid="160px"
                  />
                </Row>
              </>
            )}
          </Section>
        </Col>
      )}
    </Sidepanel>
  );
};

export default TiriPanel;
